'use client';

import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PARKING_HOVER_EVENT } from '../lib/parkingEvents';

type ParkingSpot = {
  id: string;
  street: string;
  label?: string;
  total: number;
  occupied: number;
  freeHandi?: number | null;
  freeEv?: number | null;
  coords?: [number, number];
};

type ChartMap = Record<string, number[]>;

type ParkingTableWithChartProps = {
  cityId: string;
  spots: ParkingSpot[];
  charts: ChartMap;
  selectedStreet: string;
  streetMarkerMap?: Record<string, string | undefined>;
  isLoading?: boolean;
  activeChartLoading?: boolean;
  onSpotHover?: (spotId: string, spot: ParkingSpot) => void;
  onSpotSelect?: (spotId: string, spot: ParkingSpot) => void;
  onActiveSpotChange?: (spotId: string, spot: ParkingSpot) => void;
};

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

const getSpotDisplayLabel = (spot: ParkingSpot) => {
  const trimmedLabel = spot.label?.trim();
  return trimmedLabel && trimmedLabel.length > 0 ? trimmedLabel : 'Brak nazwy';
};

const getSpotOccupancyPercentage = (spot: ParkingSpot) => {
  if (spot.total <= 0) {
    return 0;
  }

  return Math.round((spot.occupied / spot.total) * 100);
};

export function ParkingTableWithChart({
  cityId,
  spots,
  charts,
  selectedStreet,
  streetMarkerMap,
  isLoading = false,
  activeChartLoading = false,
  onSpotHover,
  onSpotSelect,
  onActiveSpotChange,
}: ParkingTableWithChartProps) {
  const computedFallbackSpot = useMemo(() => {
    if (!spots.length) {
      return null;
    }
    if (selectedStreet) {
      const matchByStreet = spots.find((spot) => spot.street === selectedStreet);
      if (matchByStreet) {
        return matchByStreet;
      }
    }
    return spots[0] ?? null;
  }, [selectedStreet, spots]);
  const computedFallbackStreet = computedFallbackSpot?.street ?? '';
  const computedFallbackId = computedFallbackSpot?.id ?? null;

  const [lockedSpotId, setLockedSpotId] = useState<string | null>(computedFallbackId);
  const [hoveredSpotId, setHoveredSpotId] = useState<string | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const fallback = ROWS_PER_PAGE_OPTIONS[1] ?? ROWS_PER_PAGE_OPTIONS[0] ?? 10;
    return fallback;
  });
  const [currentPage, setCurrentPage] = useState(0);

  const spotsSignature = useMemo(() => spots.map((spot) => spot.id).join('|'), [spots]);

  useEffect(() => {
    setLockedSpotId(computedFallbackId);
  }, [computedFallbackId]);

  useEffect(() => {
    setCurrentPage(0);
  }, [rowsPerPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [spotsSignature]);

  const lockedSpot = useMemo(() => (lockedSpotId ? spots.find((spot) => spot.id === lockedSpotId) ?? null : null), [lockedSpotId, spots]);
  const hoveredSpot = useMemo(() => (hoveredSpotId ? spots.find((spot) => spot.id === hoveredSpotId) ?? null : null), [hoveredSpotId, spots]);
  const activeSpot = hoveredSpot ?? lockedSpot ?? computedFallbackSpot;
  const activeStreet = activeSpot?.street ?? computedFallbackStreet;
  const activeSpotId = activeSpot?.id ?? null;
  const lastDispatchedMarkerId = useRef<string | null>(null);

  const dispatchHover = (markerId: string | null) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (lastDispatchedMarkerId.current === markerId) {
      return;
    }

    lastDispatchedMarkerId.current = markerId;
    window.dispatchEvent(new CustomEvent(PARKING_HOVER_EVENT, { detail: { markerId } }));
  };

  useEffect(() => {
    return () => {
      dispatchHover(null);
    };
  }, []);

  const lockedMarkerId = lockedSpot ? lockedSpot.id : null;

  useEffect(() => {
    dispatchHover(lockedMarkerId ?? null);
  }, [lockedMarkerId]);

  const totalItems = spots.length;
  const totalPages = totalItems === 0 ? 1 : Math.max(1, Math.ceil(totalItems / rowsPerPage));

  useEffect(() => {
    setCurrentPage((prev) => {
      const maxPage = Math.max(0, Math.ceil((totalItems || 1) / rowsPerPage) - 1);
      return prev > maxPage ? maxPage : prev;
    });
  }, [rowsPerPage, spotsSignature, totalItems]);

  useEffect(() => {
    if (!lockedSpotId) {
      return;
    }
    const targetIndex = spots.findIndex((spot) => spot.id === lockedSpotId);
    if (targetIndex === -1) {
      return;
    }
    const targetPage = Math.floor(targetIndex / rowsPerPage);
    setCurrentPage((prev) => (prev === targetPage ? prev : targetPage));
  }, [lockedSpotId, rowsPerPage, spotsSignature, spots]);

  const effectivePage = Math.min(currentPage, totalPages - 1);
  const pageStartIndex = effectivePage * rowsPerPage;
  const visibleSpots = spots.slice(pageStartIndex, pageStartIndex + rowsPerPage);

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const fallbackChart = charts[lockedSpot?.id ?? computedFallbackId ?? ''] ?? [];
  const activeChart = charts[activeSpotId ?? ''] ?? fallbackChart;
  const maxValue = useMemo(() => (activeChart.length ? Math.max(...activeChart) : 0), [activeChart]);
  const utilizationTicks = [100, 75, 50, 25, 0];
  const hourTicks = [0, 6, 12, 18, 23];
  const formatHourLabel = (hour: number) => `${hour}`;

  const resolveMarkerId = (spot: ParkingSpot) => streetMarkerMap?.[spot.id] ?? spot.id;

  const handleRowEnter = (spot: ParkingSpot) => {
    setHoveredSpotId(spot.id);
    const markerId = resolveMarkerId(spot);
    onSpotHover?.(spot.id, spot);
    dispatchHover(markerId ?? null);
  };

  const handleRowClick = (spot: ParkingSpot) => {
    setLockedSpotId(spot.id);
    const markerId = resolveMarkerId(spot);
    onSpotSelect?.(spot.id, spot);
    dispatchHover(markerId ?? null);
  };

  const handleRowsLeave = () => {
    setHoveredSpotId(null);
    dispatchHover(lockedMarkerId ?? null);
  };

  const rangeStart = totalItems === 0 ? 0 : pageStartIndex + 1;
  const rangeEnd = totalItems === 0 ? 0 : pageStartIndex + visibleSpots.length;

  useEffect(() => {
    if (activeSpotId && activeSpot) {
      onActiveSpotChange?.(activeSpotId, activeSpot);
    }
  }, [activeSpot, activeSpotId, onActiveSpotChange]);

  return (
    <div className="flex w-full flex-col gap-4 transition-colors max-[425px]:gap-2 max-[425px]:px-2 min-[1311px]:grid min-[1311px]:grid-cols-[7fr_3fr] min-[1311px]:items-start">
      <div
        className="relative w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-inner transition-colors dark:border-white/10 dark:bg-white/5 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.04)] min-[1311px]:col-span-1 min-[1311px]:min-w-0"
        aria-busy={isLoading}
        onMouseLeave={handleRowsLeave}
      >
        {isLoading ? (
          <div className="pointer-events-auto absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-3xl bg-white/85 text-[0.55rem] font-semibold uppercase tracking-[0.35em] text-slate-500 backdrop-blur-sm dark:bg-neutral-900/80 dark:text-slate-200">
            <span
              className="h-10 w-10 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-500 dark:border-emerald-900 dark:border-t-emerald-400"
              aria-hidden="true"
            />
            <span className="tracking-[0.5em]">Ładowanie</span>
            <span className="tracking-[0.2em] text-slate-400 dark:text-slate-300">Aktualne dane</span>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-white/70 px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-slate-400 transition-colors max-[425px]:flex-col max-[425px]:items-start max-[425px]:gap-1.5 max-[425px]:px-2 max-[425px]:py-1.5 max-[425px]:text-[8px] max-[425px]:tracking-[0.25em] dark:border-white/10 dark:bg-white/5 dark:text-slate-300/80">
          <div>{totalItems === 0 ? 'Brak wynikow' : `Wyniki ${rangeStart}-${rangeEnd} z ${totalItems}`}</div>
          <div className="flex flex-wrap items-center gap-3 max-[425px]:w-full max-[425px]:justify-between max-[425px]:gap-1.5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-slate-400 max-[425px]:gap-1 max-[425px]:text-[8px] max-[425px]:tracking-[0.25em] dark:text-slate-300/80">
              <span>Strona</span>
              <span className="text-slate-600 dark:text-slate-200">{totalItems === 0 ? '0 / 0' : `${effectivePage + 1} / ${totalPages}`}</span>
            </div>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-slate-400 max-[425px]:gap-1 max-[425px]:text-[8px] max-[425px]:tracking-[0.25em] dark:text-slate-300/80">
              <span>Na stronie</span>
              <div className="relative">
                <select
                  className="appearance-none rounded-xl border border-slate-200 bg-white px-3 py-1 pr-8 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-600 transition-colors max-[425px]:px-1.5 max-[425px]:py-0.5 max-[425px]:pr-5 max-[425px]:text-[9px] max-[425px]:tracking-[0.15em] [color-scheme:light] dark:border-white/10 dark:bg-neutral-900 dark:text-slate-100 dark:[color-scheme:dark]"
                  value={rowsPerPage}
                  onChange={(event) => {
                    const next = Number.parseInt(event.target.value, 10);
                    if (Number.isNaN(next) || next <= 0) {
                      return;
                    }
                    setRowsPerPage(next);
                  }}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 max-[425px]:right-1.5 dark:text-slate-300">
                  <svg className="h-3 w-3 max-[425px]:h-2.5 max-[425px]:w-2.5" viewBox="0 0 12 8" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2 2l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </label>
            <div className="flex items-center gap-2 max-[425px]:gap-1.5">
              <button
                type="button"
                onClick={goToPreviousPage}
                disabled={effectivePage === 0 || totalItems === 0}
                className="rounded-full border border-slate-200 p-2 text-slate-400 transition hover:border-emerald-500 hover:text-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 max-[425px]:p-1 dark:border-white/10 dark:text-slate-300/80 dark:hover:border-emerald-400 dark:hover:text-emerald-300"
                aria-label="Poprzednia strona"
              >
                {'<'}
              </button>
              <button
                type="button"
                onClick={goToNextPage}
                disabled={effectivePage >= totalPages - 1 || totalItems === 0}
                className="rounded-full border border-slate-200 p-2 text-slate-400 transition hover:border-emerald-500 hover:text-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 max-[425px]:p-1 dark:border-white/10 dark:text-slate-300/80 dark:hover:border-emerald-400 dark:hover:text-emerald-300"
                aria-label="Nastepna strona"
              >
                {'>'}
              </button>
            </div>
          </div>
        </div>
        <div className="space-y-3 p-3 md:hidden">
          {visibleSpots.length === 0 ? (
            <div className="rounded-[26px] border border-dashed border-slate-300 bg-slate-50/70 px-4 py-8 text-center text-sm font-medium text-slate-500 dark:border-white/15 dark:bg-white/[0.04] dark:text-slate-300/80">
              Brak danych do wyświetlenia.
            </div>
          ) : (
            visibleSpots.map((spot, index) => {
              const zebraIndex = pageStartIndex + index;
              const rowFreeSpaces = spot.total - spot.occupied;
              const occupancyPercentage = getSpotOccupancyPercentage(spot);
              const isLocked = spot.id === lockedSpotId;
              const isHovered = spot.id === hoveredSpotId;
              const emphasize = isHovered || isLocked;
              const markerId = resolveMarkerId(spot);
              const actionHref = `/parking/${encodeURIComponent(cityId)}/${encodeURIComponent(spot.id)}`;
              const cardClasses = [
                'rounded-[26px] border p-4 text-left shadow-[0_18px_40px_-32px_rgba(15,23,42,0.35)] transition-all duration-200',
                isLocked
                  ? 'border-emerald-400 bg-emerald-50/90 ring-1 ring-emerald-400 dark:border-emerald-400/70 dark:bg-emerald-500/10 dark:ring-emerald-400/60'
                  : zebraIndex % 2 === 0
                  ? 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.05]'
                  : 'border-slate-200 bg-slate-50/80 dark:border-white/10 dark:bg-white/[0.04]',
                'active:scale-[0.99]',
                emphasize ? 'shadow-[0_22px_45px_-30px_rgba(16,185,129,0.45)]' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <div
                  key={spot.id}
                  onMouseEnter={() => handleRowEnter(spot)}
                  onClick={() => handleRowClick(spot)}
                  className={cardClasses}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Space' || event.key === 'Spacebar') {
                      event.preventDefault();
                      handleRowClick(spot);
                    }
                  }}
                  aria-pressed={isLocked}
                  data-marker-id={markerId ?? undefined}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] ${
                            isLocked
                              ? 'border-emerald-500 bg-emerald-500 text-white dark:border-emerald-400 dark:bg-emerald-400/90 dark:text-slate-950'
                              : 'border-slate-200 bg-white text-slate-500 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-300/80'
                          }`}
                        >
                          {isLocked ? 'Aktywny' : `Zajętość ${occupancyPercentage}%`}
                        </span>
                      </div>
                      <p
                        className={`mt-3 text-sm font-semibold uppercase leading-snug tracking-[0.18em] ${
                          emphasize ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-100'
                        }`}
                      >
                        {getSpotDisplayLabel(spot)}
                      </p>
                      <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/75">{spot.street}</p>
                    </div>
                    <Link
                      to={actionHref}
                      onClick={(event) => event.stopPropagation()}
                      className="shrink-0 rounded-xl border border-emerald-500 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white dark:border-emerald-400 dark:text-emerald-300 dark:hover:bg-emerald-400/20"
                    >
                      Szczegóły
                    </Link>
                  </div>

                  <div className="mt-4">
                    <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                      <div className="h-full rounded-full bg-rose-500 transition-[width] duration-200" style={{ width: `${occupancyPercentage}%` }} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-300/75">
                      <span>{occupancyPercentage}% zajęte</span>
                      <span className="text-emerald-600 dark:text-emerald-300">{rowFreeSpaces} wolnych</span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <div className="rounded-2xl border border-slate-200 bg-white/85 px-3 py-3 text-center dark:border-white/10 dark:bg-white/[0.05]">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-300/75">Miejsca</p>
                      <p className="mt-2 text-lg font-semibold text-slate-700 dark:text-slate-100">{spot.total}</p>
                    </div>
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-3 text-center dark:border-rose-400/20 dark:bg-rose-500/10">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-rose-500 dark:text-rose-200/85">Zajęte</p>
                      <p className="mt-2 text-lg font-semibold text-rose-600 dark:text-rose-200">{spot.occupied}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-center dark:border-emerald-400/20 dark:bg-emerald-500/10">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-200/85">Wolne</p>
                      <p className="mt-2 text-lg font-semibold text-emerald-600 dark:text-emerald-200">{rowFreeSpaces}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-700 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-200">
                      Niepełn.: {spot.freeHandi ?? '--'}
                    </span>
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200">
                      EV: {spot.freeEv ?? '--'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="hidden overflow-x-auto pb-4 md:block">
          <div className="min-w-[1120px]">
            <div className="grid grid-cols-[120px_minmax(190px,1.05fr)_minmax(150px,1fr)_130px_90px_90px_110px_110px] bg-slate-100 text-[0.6rem] font-semibold uppercase tracking-[0.35em] text-slate-500 transition-colors dark:bg-white/10 dark:text-slate-300 sm:text-[0.65rem] md:text-[0.7rem] max-[1590px]:text-[0.55rem] max-[1532px]:grid-cols-[110px_minmax(150px,1fr)_minmax(120px,0.95fr)_105px_80px_80px_95px_95px]">
              <div className="px-4 py-3 text-left sm:py-4">Akcje</div>
              <div className="px-4 py-3 text-left sm:py-4">Nazwa</div>
              <div className="px-4 py-3 text-left sm:py-4">Ulica</div>
              <div className="px-3 py-3 text-center sm:py-4">Ilość miejsc parkingowych</div>
              <div className="px-3 py-3 text-center text-rose-500 dark:text-rose-300 sm:py-4">Zajęte</div>
              <div className="px-3 py-3 text-center text-emerald-600 dark:text-emerald-300 sm:py-4">Wolne</div>
              <div className="px-3 py-3 text-center text-sky-600 dark:text-sky-300 sm:py-4">Wolne niepełn.</div>
              <div className="px-3 py-3 text-center text-amber-600 dark:text-amber-300 sm:py-4">Wolne EV</div>
            </div>
            <div className="divide-y divide-slate-200 transition-colors dark:divide-white/10">
              {visibleSpots.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm font-medium text-slate-500 dark:text-slate-300/80 [grid-column:1/-1] max-[1590px]:text-xs">
                  Brak danych do wyświetlenia.
                </div>
              ) : (
                visibleSpots.map((spot, index) => {
                  const zebraIndex = pageStartIndex + index;
                  const rowFreeSpaces = spot.total - spot.occupied;
                  const isLocked = spot.id === lockedSpotId;
                  const isHovered = spot.id === hoveredSpotId;
                  const emphasize = isHovered || isLocked;
                  const baseTone = isLocked
                    ? 'bg-slate-200/80 dark:bg-emerald-500/20'
                    : zebraIndex % 2 === 0
                    ? 'bg-white dark:bg-white/5'
                    : 'bg-slate-50 dark:bg-white/[0.04]';
                  const rowClasses = [
                    'relative grid grid-cols-[120px_minmax(190px,1.05fr)_minmax(150px,1fr)_130px_90px_90px_110px_110px] cursor-pointer text-xs font-medium text-slate-700 transition-colors duration-200 dark:text-slate-100 sm:text-sm max-[1590px]:text-[0.7rem] max-[1532px]:grid-cols-[110px_minmax(150px,1fr)_minmax(120px,0.95fr)_105px_80px_80px_95px_95px]',
                    baseTone,
                    'hover:bg-emerald-50 hover:shadow-[0_18px_35px_-30px_rgba(16,185,129,0.6)] dark:hover:bg-emerald-500/20 dark:hover:shadow-[0_18px_35px_-30px_rgba(16,185,129,0.35)]',
                    emphasize
                      ? 'bg-emerald-50 shadow-[0_18px_35px_-30px_rgba(16,185,129,0.6)] ring-1 ring-emerald-400 dark:bg-emerald-500/20 dark:shadow-[0_18px_35px_-30px_rgba(16,185,129,0.35)] dark:ring-emerald-400/70'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  const markerId = resolveMarkerId(spot);
                  const actionHref = `/parking/${encodeURIComponent(cityId)}/${encodeURIComponent(spot.id)}`;

                  return (
                    <div
                      key={spot.id}
                      onMouseEnter={() => handleRowEnter(spot)}
                      onClick={() => handleRowClick(spot)}
                      className={rowClasses}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Space' || event.key === 'Spacebar') {
                          event.preventDefault();
                          handleRowClick(spot);
                        }
                      }}
                      aria-pressed={isLocked}
                      data-marker-id={markerId ?? undefined}
                    >
                      <div className="flex items-center justify-start px-3 py-3 sm:py-4">
                        <Link
                          to={actionHref}
                          onClick={(event) => event.stopPropagation()}
                          className="rounded-xl border border-emerald-500 px-1 py-1 text-[0.55rem] font-semibold uppercase tracking-[0.25em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white dark:border-emerald-400 dark:text-emerald-300 dark:hover:bg-emerald-400/20 sm:text-[0.65rem] md:text-[0.7rem] max-[1590px]:text-[0.5rem]"
                        >
                          Szczegóły
                        </Link>
                      </div>
                      <div
                        className={`flex items-center px-4 py-3 text-[0.8rem] font-semibold transition-colors duration-200 sm:py-4 sm:text-base max-[1590px]:text-[0.75rem] ${
                          emphasize ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-100'
                        }`}
                      >
                        <span className="truncate leading-tight">{getSpotDisplayLabel(spot)}</span>
                      </div>
                      <div
                        className={`flex items-center px-4 py-3 uppercase tracking-[0.08em] transition-colors duration-200 sm:py-4 ${
                          emphasize ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-600 dark:text-slate-200'
                        }`}
                      >
                        <span className="truncate text-[0.75rem] sm:text-base max-[1590px]:text-[0.7rem]">{spot.street}</span>
                      </div>
                      <div
                        className={`flex items-center justify-center px-3 py-3 text-sm font-semibold transition-colors duration-200 sm:py-4 sm:text-base max-[1590px]:text-[0.75rem] ${
                          emphasize ? 'text-emerald-600 dark:text-emerald-300' : 'dark:text-slate-200'
                        }`}
                      >
                        {spot.total}
                      </div>
                      <div
                        className={`flex items-center justify-center bg-rose-500 px-3 py-3 text-sm font-semibold text-white transition-opacity duration-200 sm:py-4 sm:text-base max-[1590px]:text-[0.75rem] dark:bg-rose-500/90 ${
                          emphasize ? 'opacity-100' : 'opacity-80'
                        }`}
                      >
                        {spot.occupied}
                      </div>
                      <div
                        className={`flex items-center justify-center bg-emerald-500 px-3 py-3 text-sm font-semibold text-white transition-opacity duration-200 sm:py-4 sm:text-base max-[1590px]:text-[0.75rem] dark:bg-emerald-500/90 ${
                          emphasize ? 'opacity-100' : 'opacity-80'
                        }`}
                      >
                        {rowFreeSpaces}
                      </div>
                      <div
                        className={`flex items-center justify-center px-3 py-3 text-sm font-semibold transition-colors duration-200 sm:py-4 sm:text-base max-[1590px]:text-[0.75rem] ${
                          spot.freeHandi === null || typeof spot.freeHandi === 'undefined'
                            ? 'text-slate-400 dark:text-slate-400/80'
                            : emphasize
                            ? 'text-emerald-600 dark:text-emerald-300'
                            : 'text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {spot.freeHandi ?? '--'}
                      </div>
                      <div
                        className={`flex items-center justify-center px-3 py-3 text-sm font-semibold transition-colors duration-200 sm:py-4 sm:text-base max-[1590px]:text-[0.75rem] ${
                          spot.freeEv === null || typeof spot.freeEv === 'undefined'
                            ? 'text-slate-400 dark:text-slate-400/80'
                            : emphasize
                            ? 'text-emerald-600 dark:text-emerald-300'
                            : 'text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {spot.freeEv ?? '--'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col rounded-3xl border border-slate-200 bg-slate-50 px-4 py-5 shadow-inner transition-colors max-[425px]:rounded-2xl max-[425px]:px-3 max-[425px]:py-3 dark:border-white/10 dark:bg-white/5 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.04)] min-[1311px]:col-span-1 min-[1311px]:min-w-0">
        <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.4em] text-slate-400 dark:text-slate-300/80">
          <span>Analiza zajętości</span>
          <span className="text-[11px] tracking-[0.2em] text-slate-600 dark:text-slate-200">{activeStreet.toUpperCase()}</span>
        </div>
        <div className="relative mt-6 overflow-hidden rounded-2xl border border-slate-100 bg-white/60 shadow-inner max-[425px]:mt-5 dark:border-white/10 dark:bg-white/5">
          <div className="flex items-center justify-between px-4 pt-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300/80">
            <span>Procent zajętości</span>
          </div>
          <div className="mt-2 flex items-stretch gap-3 px-4 pb-5">
            <div className="flex w-5 shrink-0 flex-col justify-between text-right text-[10px] text-slate-400 dark:text-slate-400/80">
              {utilizationTicks.map((tick) => (
                <span key={`tick-${tick}`}>{tick}%</span>
              ))}
            </div>
            <div className="relative flex-1">
              <div className="pointer-events-none absolute inset-0 flex flex-col justify-between">
                {utilizationTicks.map((tick) => (
                  <span key={`grid-${tick}`} className="h-px w-full bg-slate-200/60 dark:bg-white/10" />
                ))}
              </div>
              <div className="relative flex h-[180px] items-end gap-[4px] sm:h-[200px]">
                {activeChart.map((value, index) => {
                  const isPeak = value === maxValue && maxValue > 0;
                  const hourLabel = formatHourLabel(index);
                  return (
                    <div
                      key={`${activeStreet}-${index}`}
                      className={`flex-1 rounded-t transition-colors duration-200 ${isPeak ? "bg-emerald-500" : "bg-slate-400 dark:bg-slate-600"}`}
                      style={{ height: `${value}%` }}
                      title={`${hourLabel} : ${value}% zajęte`}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div className="px-4 pb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-400/80">
            <div className="ml-[3.25rem] flex justify-between">
              {hourTicks.map((tick) => (
                <span key={`hour-${tick}`}>{formatHourLabel(tick)}</span>
              ))}
            </div>
          </div>
          {activeChartLoading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/85 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 backdrop-blur-sm dark:bg-neutral-900/80 dark:text-slate-200">
              <span
                className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-500 dark:border-emerald-900 dark:border-t-emerald-400"
                aria-hidden="true"
              />
              <span>Ładowanie</span>
            </div>
          ) : null}
        </div>
        <p className="mt-4 text-[11px] leading-relaxed text-slate-500 dark:text-slate-300/80">Analiza zajętości miejsc parkingowych z ostatnich 24h.</p>
      </div>
    </div>
  );
}
